// @ts-nocheck
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import Block, { BlockProps } from "@store/Library/Components/Toolkit/Block";
import { infoProps } from "@store/Themes/common/props/info";
import { orderProps } from "@store/Themes/common/props/order";
import getWidgetComponent from "@toolkit/Framework/Common/Utilities/getWidgetComponent";
import { useSession, useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import { useBillToState } from "@toolkit/Framework/Store/Data/BillTos/BillTosHooks";
import { useCartState } from "@toolkit/Framework/Store/Data/Carts/CartsHooks";
import { useOnePageCheckoutConfirmation } from "@toolkit/Framework/Store/Toolkit/Pages/OnePageCheckout/OnePageCheckoutHooks";
import React, { FC } from "react";

/* Store Custom Code - Start */
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import { css } from "styled-components";
import { checkoutProps } from "@store/Themes/common/props/checkout";
import siteMessage from "@insite/client-framework/SiteMessage";
import getColor from "@insite/mobius/utilities/getColor";
/* Store Custom Code - End */

const PaymentMethod = getWidgetComponent("OnePageCheckoutConfirmation/OnePageCheckoutConfirmationPaymentMethod");

export interface OnePageCheckoutConfirmationOrderInformationProps extends WidgetProps {}

export interface OnePageCheckoutConfirmationOrderInformationStyles {
    orderNumberHeading?: TypographyProps;
    orderNumberText?: TypographyPresentationProps;
    vatNumberGridItem?: GridItemProps;
    notesGridItem?: GridItemProps;
    orderDetailsGridContainer?: GridContainerProps;
    statusGridItem?: GridItemProps;
    orderDateGridItem?: GridItemProps;
    poNumberGridItem?: GridItemProps;
    headingAndTextStyles?: SmallHeadingAndTextStyles;
    // Store Custom Code
    shippingInstructionsHeadingAndTextStyles?: SmallHeadingAndTextStyles;
    block?: BlockProps;
    deliveryMethodGridItem?: GridItemProps;
    paymentMethodGridItem?: GridItemProps;
    // Store Custom Code
    contentWrapper?: InjectableCss;
    deliveryDateGridItem?: GridItemProps;
    deliveryDateHintMessage?: TypographyProps;
}

export const onePageCheckoutConfirmationOrderInformationStyles: OnePageCheckoutConfirmationOrderInformationStyles = {
    block: orderProps.block,
    orderNumberHeading: orderProps.subtitle,
    headingAndTextStyles: {
        heading: infoProps.label,
        /* Store Custom Code - Start */
        text: {
            ...infoProps.value,
            css: css`
                ${infoProps.value?.css}
                font-size: ${x => x.theme.design.typography.size.smaller};
                line-height: ${x => x.theme.design.typography.lineHeight.small};
            `,
        },
        /* Store Custom Code - End */
    },
    /* Store Custom Code - Start */
    shippingInstructionsHeadingAndTextStyles: {
        heading: infoProps.label,
        text: {
            ...infoProps.value,
            css: css`
                ${infoProps.value?.css}
                font-size: ${x => x.theme.design.typography.size.smaller};
                line-height: ${x => x.theme.design.typography.lineHeight.small};
                white-space: pre-wrap;
            `,
        },
    },
    /* Store Custom Code - End */
    orderDetailsGridContainer: infoProps.grid?.container,
    orderDateGridItem: infoProps.grid?.item,
    poNumberGridItem: infoProps.grid?.item,
    statusGridItem: infoProps.grid?.item,
    vatNumberGridItem: infoProps.grid?.item,
    deliveryMethodGridItem: infoProps.grid?.item,
    paymentMethodGridItem: infoProps.grid?.item,
    /* Store Custom Code - Start */
    notesGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            display: block;
        `,
    },
    contentWrapper: {
        ...checkoutProps.contentWrapper,
        css: css`
            ${checkoutProps.contentWrapper?.css}
            margin-bottom: ${pxToRem(16)};
        `,
    },
    deliveryDateGridItem: {
        width: [12, 12, 6, 6, 6],
        css: css`
            display: block;
        `,
    },
    deliveryDateHintMessage: {
        css: css`
            font-size: ${x => x.theme.design.typography.size.smaller};
            line-height: ${x => x.theme.design.typography.lineHeight.small};
            color: ${getColor("text.accentAlt")};
            margin-top: ${pxToRem(5)};
            display: block;
        `,
    },
    /* Store Custom Code - End */
};

const styles = onePageCheckoutConfirmationOrderInformationStyles;

const OnePageCheckoutConfirmationOrderInformation: FC<OnePageCheckoutConfirmationOrderInformationProps> = () => {
    const { cartId } = useOnePageCheckoutConfirmation();
    const cartState = useCartState(cartId).value;
    const { enableVat } = useSettingsCollection().productSettings;
    const billTo = useBillToState(cartState?.billToId).value;
    const { language } = useSession();

    if (!cartState || !billTo) {
        return null;
    }

    return (
        <StyledWrapper /* Store Custom Code */>
            <StyledWrapper {...styles.contentWrapper} /* Store Custom Code */>
                <Typography {...styles.orderNumberHeading}>
                    {/* Store Custom Code - Start */}
                    {`${translate("Temporary Order")} #`}
                    {/* Store Custom Code - End */}
                    <Typography {...styles.orderNumberText}>
                        {cartState.erpOrderNumber || cartState.orderNumber}
                    </Typography>
                </Typography>
                <GridContainer {...styles.orderDetailsGridContainer}>
                    <GridItem {...styles.orderDateGridItem}>
                        <SmallHeadingAndText
                            extendedStyles={styles.headingAndTextStyles}
                            heading={translate("Order Date")}
                            text={
                                cartState?.orderDate
                                    ? getLocalizedDateTime({
                                          dateTime: new Date(cartState.orderDate as Date),
                                          language,
                                      })
                                    : ""
                            }
                        />
                    </GridItem>
                    {cartState.poNumber ? (
                        <GridItem {...styles.poNumberGridItem}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("PO Number")}
                                text={cartState.poNumber}
                            />
                        </GridItem>
                    ) : null}
                    {/* Store Removed - cartState status */}
                    {enableVat && cartState.customerVatNumber ? (
                        <GridItem {...styles.vatNumberGridItem}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("VAT Number")}
                                text={cartState.customerVatNumber}
                            />
                        </GridItem>
                    ) : null}
                </GridContainer>
            </StyledWrapper>
            <StyledWrapper {...styles.contentWrapper} /* Store Custom Code */>
                {/* Store Custom Code - Start */}
                <Typography {...styles.orderNumberHeading}>{translate("Shipping Details")}</Typography>
                {/* Store Custom Code - End */}
                <GridContainer {...styles.orderDetailsGridContainer} /* Store Custom Code */>
                    {/* Store Removed - deliveryMethodGridItem */}
                    {/* Store Custom Code - Start */}
                    <GridItem {...styles.paymentMethodGridItem}>
                        <PaymentMethod />
                    </GridItem>
                    {/* Store Custom Code - End */}
                    {/* Store Move - PaymentMethod - Up */}
                </GridContainer>
                <GridContainer {...styles.orderDetailsGridContainer}>
                    {/* Store Custom Code - Start */}
                    {cartState.requestedDeliveryDate ? (
                        <GridItem {...styles.deliveryDateGridItem}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Requested ship date")}
                                text={getLocalizedDateTime({
                                    dateTime: new Date(cartState.requestedDeliveryDate),
                                    language,
                                })}
                            />
                            <Typography {...styles.deliveryDateHintMessage}>
                                {siteMessage("Checkout_RequestedDeliveryDateInformation")}
                            </Typography>
                        </GridItem>
                    ) : null}
                    {/* Store Custom Code - End */}
                    {cartState.notes ? (
                        <GridItem {...styles.notesGridItem}>
                            <SmallHeadingAndText
                                extendedStyles={styles.headingAndTextStyles}
                                heading={translate("Order Note")} // Store Custom Code
                                text={cartState.notes}
                            />
                        </GridItem>
                    ) : null}
                    {/* Store Custom Code - Start */}
                    {cartState.properties?.shippingInstructions ? (
                        <GridItem {...styles.notesGridItem}>
                            <SmallHeadingAndText
                                extendedStyles={styles.shippingInstructionsHeadingAndTextStyles}
                                heading={translate("Shipping Instructions")}
                                text={cartState.properties.shippingInstructions}
                            />
                        </GridItem>
                    ) : null}
                    {/* Store Custom Code - End */}
                </GridContainer>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: OnePageCheckoutConfirmationOrderInformation,
    definition: {
        displayName: "Order Information",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
        group: "One Page Checkout Confirmation",
    },
};

export default widgetModule;
